// Shared file-header-reading + fuzzy field-matching logic, used by
// ExportConfigPanel.js ("auto-configure from a file"), LeadDeliverySetup.js's
// DestinationForm ("auto-match field mapping from a file or a saved export
// config"), and ClientListImportWizard.js's Step 2 (reads a whole multi-sheet
// client workbook via readWorkbookSheetNames()/readSheetHeaderRow() — see
// MAX_WORKBOOK_BYTES below, a separate/higher cap from the other two
// header-only callers' MAX_UPLOAD_BYTES). Originally written once in
// ExportConfigPanel.js; extracted here so later uses didn't duplicate it —
// this codebase has been bitten by hand-duplicated logic drifting apart
// before (see CLAUDE.md's DeliveryCard note).
import * as XLSX from 'xlsx';

// Minimal quoted-comma-aware CSV line splitter — only ever used on the header
// row, not the whole file, so a full CSV parser dependency isn't warranted.
function parseCsvLine(line) {
  const result = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') { result.push(cur); cur = ''; }
    else cur += ch;
  }
  result.push(cur);
  return result.map(s => s.trim());
}

// Hard ceiling on anything we'll parse in the browser. The original two
// callers (ExportConfigPanel.js, LeadDeliverySetup.js) only ever consume a
// header row or a small client-supplied mapping doc — nothing legitimate is
// anywhere near this. Without a cap, a malicious (or just enormous) file
// parses unbounded on the main thread and hangs the tab.
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

// ClientListImportWizard.js's Step 2 reads a whole multi-sheet client
// workbook via readWorkbookSheetNames()/readSheetHeaderRow() below — a
// genuinely large-file use case this module's original 15MB cap was never
// designed for (a real one blocked a real 17MB file, 2026-08-28). This cap is
// deliberately paired with the backend's own: POST /import-jobs passes an
// explicit MAX_CLIENT_LIST_BYTES (also 100MB) rather than the shared 10MB
// default in app/utils/uploads.py. The two MUST stay in step — when the
// 2026-09-23 security review applied that 10MB default to the endpoint, this
// side still accepted 100MB, so a large workbook mapped fine in Step 2 and
// then 413'd on submit. nginx's client_max_body_size is a third gate above
// both and targets the same 100m ceiling end to end.
const MAX_WORKBOOK_BYTES = 100 * 1024 * 1024;

// Enough rows to skip leading blank/spacer rows before the real header without
// parsing an entire workbook to find row 1. Deliberately NOT 1 — real client
// templates do have blank leading rows, and `blankrows: false` would then leave
// us with nothing.
const HEADER_SCAN_ROWS = 50;

// XLSX/ZIP files start with the local file header signature "PK\x03\x04".
const isZipContent = (bytes) =>
  bytes.length > 3 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;

// Decode as UTF-8, dropping a leading BOM — matches what FileReader.readAsText
// did before this read everything as an ArrayBuffer.
const decodeText = (bytes) => {
  const text = new TextDecoder('utf-8').decode(bytes);
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
};

// Reads a file's bytes once, with the size cap applied before anything parses.
// maxBytes defaults to the conservative header-only cap — callers reading a
// whole workbook (not just its header row) pass MAX_WORKBOOK_BYTES instead.
function readBytes(file, maxBytes = MAX_UPLOAD_BYTES) {
  return new Promise((resolve, reject) => {
    if (file && file.size > maxBytes) {
      reject(new Error(
        `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). ` +
        `The limit is ${maxBytes / 1024 / 1024} MB.`
      ));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = (e) => resolve(new Uint8Array(e.target.result));
    reader.readAsArrayBuffer(file);
  });
}

// Whether a file is really a workbook is decided by its CONTENT, not its name.
// A client sending a genuine .xlsx saved as "leads.csv" is common enough that
// the backend already hit it and 500'd (see backend CLAUDE.md's bytes_to_csv_text
// note); here the filename check silently produced raw ZIP bytes as "headers".
const parseSheetRows = (bytes, { maxRows, sheetName } = {}) => {
  const opts = { type: 'array' };
  if (maxRows) opts.sheetRows = maxRows;
  const wb = XLSX.read(bytes, opts);
  const sheet = wb.Sheets[sheetName || wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
};

// Reads just the header row of an uploaded CSV or XLSX file. Returns a Promise
// resolving to string[] (raw header cells, in file order).
export function readFileHeaderRow(file) {
  return readBytes(file).then((bytes) => {
    if (isZipContent(bytes)) {
      const rows = parseSheetRows(bytes, { maxRows: HEADER_SCAN_ROWS });
      return (rows[0] || []).map(v => String(v ?? '').trim()).filter(Boolean);
    }
    const firstLine = decodeText(bytes).split(/\r\n|\n|\r/)[0] || '';
    return parseCsvLine(firstLine).filter(Boolean);
  });
}

// Sheet names of an uploaded workbook, in file order — for a "pick a sheet"
// step on a multi-tab client file (e.g. one region per tab). Returns [] for
// a CSV (no sheet concept — caller treats that as "use the file directly, no
// picker needed"), not a rejection.
export function readWorkbookSheetNames(file) {
  return readBytes(file, MAX_WORKBOOK_BYTES).then((bytes) => {
    if (!isZipContent(bytes)) return [];
    const wb = XLSX.read(bytes, { type: 'array', bookType: 'xlsx' });
    return wb.SheetNames;
  });
}

// Same contract as readFileHeaderRow(), but for one named sheet instead of
// always the first — used once a PM has picked which tab of a multi-sheet
// workbook to map columns from. Falls back to the plain CSV path when the
// file isn't a workbook at all (sheetName is meaningless there).
export function readSheetHeaderRow(file, sheetName) {
  return readBytes(file, MAX_WORKBOOK_BYTES).then((bytes) => {
    if (!isZipContent(bytes)) {
      const firstLine = decodeText(bytes).split(/\r\n|\n|\r/)[0] || '';
      return parseCsvLine(firstLine).filter(Boolean);
    }
    const rows = parseSheetRows(bytes, { maxRows: HEADER_SCAN_ROWS, sheetName });
    return (rows[0] || []).map(v => String(v ?? '').trim()).filter(Boolean);
  });
}

export const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Small Levenshtein distance — headers/field lists are short, so an O(m*n) DP
// table is negligible here; no fuzzy-matching dependency needed.
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}
const similarity = (a, b) => {
  const maxLen = Math.max(a.length, b.length);
  return maxLen === 0 ? 1 : 1 - levenshtein(a, b) / maxLen;
};

// Best-guess match for one string (an uploaded header, or a target field name)
// against a list of {key, label} candidates. Exact normalized match on
// label/key scores highest, substring match next, otherwise a dampened
// fuzzy-similarity score. Returns null below a 0.55 confidence floor — an
// unmatched item should stay unmatched, not get forced onto whatever
// candidate happened to score highest.
export function bestFieldMatch(header, fields) {
  const normHeader = normalize(header);
  if (!normHeader) return null;
  let best = null;
  let bestScore = 0;
  for (const f of fields) {
    for (const candidate of [normalize(f.label), normalize(f.key)]) {
      if (!candidate) continue;
      let score;
      if (candidate === normHeader) score = 1;
      else if (candidate.includes(normHeader) || normHeader.includes(candidate)) score = 0.85;
      else score = similarity(candidate, normHeader) * 0.8;
      if (score > bestScore) { bestScore = score; best = f; }
    }
  }
  return bestScore >= 0.55 ? { field: best, score: bestScore } : null;
}

export const confidenceLabel = (score) => (score >= 0.95 ? 'Exact match' : score >= 0.8 ? 'Likely match' : 'Possible match');

// Reads every row of an uploaded client-provided field-mapping doc (CSV or XLSX),
// e.g. "Lead Template and Mapping.xlsx" — a 4-column sheet: our friendly label /
// their target field name / notes / a literal value. Returns a Promise resolving
// to string[][] (every row after the header, in file order, blank rows dropped).
// Shares readFileHeaderRow()'s size cap and content sniffing, but returns every
// row instead of just row 0 — so no sheetRows limit here; MAX_UPLOAD_BYTES is
// what bounds the work.
export function readMappingDocRows(file) {
  return readBytes(file).then((bytes) => {
    if (isZipContent(bytes)) {
      const rows = parseSheetRows(bytes);
      return rows.slice(1).map(r => (r || []).map(v => String(v ?? '').trim())).filter(r => r.some(Boolean));
    }
    const lines = decodeText(bytes).split(/\r\n|\n|\r/).slice(1);
    return lines.map(parseCsvLine).filter(r => r.some(Boolean));
  });
}

// A value like "[Confirming with Armis]" is a placeholder the client hasn't
// actually confirmed yet, not a real literal — never treat it as one.
const isPlaceholderValue = (v) => /^\[.*\]$/.test((v || '').trim());

// Classifies every row from readMappingDocRows() into exactly one of 3 buckets,
// using column position (0=our label / source column header, 1=their target
// field name, 2=notes, 3=literal value) rather than header text — a future
// client's doc with slightly different column wording still parses correctly.
//
// Returns { mapping: [{key, value}], fixed: [{key, value}], pending: [{key, label, note, value}] }
// - mapping: target field -> the source column header this row says will supply it
// - fixed:   target field -> a literal value, always sent regardless of source data
//            (Notes says "HARD VALUE" and Value(s) is a real, non-placeholder literal)
// - pending: target field the client hasn't confirmed a value for yet (Value(s) is
//            a bracketed placeholder like "[Confirming with Armis]") — left
//            unmapped rather than guessed at, surfaced in the import summary instead
export function classifyMappingDocRows(rows) {
  const mapping = [];
  const fixed = [];
  const pending = [];
  for (const row of rows) {
    const [label = '', field = '', notes = '', value = ''] = row;
    if (!field) continue;
    const isHardValue = /hard value/i.test(notes);
    if (isPlaceholderValue(value)) {
      pending.push({ key: field, label, note: notes, value });
    } else if (isHardValue && value) {
      fixed.push({ key: field, value });
    } else {
      mapping.push({ key: field, value: label });
    }
  }
  return { mapping, fixed, pending };
}
